import api from '../utils/apiClient';
import { API_ENDPOINTS } from '../config/api';
import {
  saveRoomToLocalStorage,
  getRoomFromLocalStorage,
  getAllRoomsFromLocalStorage,
  removeRoomFromLocalStorage,
  updateRoomInLocalStorage,
  addChatMessageToRoom,
  updateParticipantReadyStatus
} from '../utils/localStorageHelper';

/**
 * Service for handling interactions with the Duel API
 */
const duelService = {
  /**
   * Get player statistics
   * @param {string} userId - User ID
   * @returns {Promise} - API response with player stats
   */
  getPlayerStats: async (userId) => {
    try {
      // Create mock stats data for when the API is unavailable
      const mockStats = {
        userId,
        username: userId.substring(0, 6) + '...',
        rating: 1200 + Math.floor(Math.random() * 400),
        wins: Math.floor(Math.random() * 20),
        losses: Math.floor(Math.random() * 10),
        draws: Math.floor(Math.random() * 5),
        matchesPlayed: Math.floor(Math.random() * 35),
        rank: Math.floor(Math.random() * 50) + 1,
        success: true,
      };
      
      try {
        const statsResponse = await api.get(API_ENDPOINTS.duel.stats(userId));
        return statsResponse?.data ? statsResponse : { success: true, data: mockStats };
      } catch (apiError) {
        console.warn('API error getting stats, using mock data:', apiError);
        return { success: true, data: mockStats };
      }
    } catch (error) {
      console.error('Error getting player stats:', error);
      throw error;
    }
  },

  /**
   * Get leaderboard
   * @param {number} limit - Number of players to return (default 10)
   * @returns {Promise} - API response with leaderboard data
   */
  getLeaderboard: async (limit = 10) => {
    try {
      // Create mock leaderboard data for when the API is unavailable
      const mockLeaderboard = [
        { userId: 'player1', username: 'PoolPro', rating: 1850, wins: 42, losses: 10 },
        { userId: 'player2', username: 'Challenger', rating: 1720, wins: 35, losses: 15 },
        { userId: 'player3', username: 'CueMaster', rating: 1690, wins: 28, losses: 12 },
        { userId: 'player4', username: 'BilliardKing', rating: 1620, wins: 25, losses: 11 },
        { userId: 'player5', username: 'PoolShark', rating: 1585, wins: 22, losses: 9 },
        { userId: 'player6', username: 'StickWizard', rating: 1520, wins: 19, losses: 14 },
        { userId: 'player7', username: 'CueChampion', rating: 1490, wins: 17, losses: 15 },
        { userId: 'player8', username: 'TableMaster', rating: 1420, wins: 15, losses: 11 },
        { userId: 'player9', username: 'RackAttack', rating: 1380, wins: 12, losses: 10 },
        { userId: 'player10', username: 'PocketPro', rating: 1350, wins: 10, losses: 8 }
      ].slice(0, limit);
      
      try {
        const leaderboardResponse = await api.get(`${API_ENDPOINTS.duel.leaderboard}?limit=${limit}`);
        return leaderboardResponse?.data ? leaderboardResponse : { success: true, data: mockLeaderboard };
      } catch (apiError) {
        console.warn('API error getting leaderboard, using mock data:', apiError);
        return { success: true, data: mockLeaderboard };
      }
    } catch (error) {
      console.error('Error getting leaderboard:', error);
      throw error;
    }
  },

  /**
   * Create a duel room
   * @param {Object} data - Room data
   * @param {string} data.userId - User ID of the room creator
   * @param {string} data.username - Username of the room creator
   * @param {string} data.roomName - Name of the room
   * @param {number} data.maxParticipants - Maximum number of participants (must be even)
   * @returns {Promise} - API response with room data
   */
  createRoom: async (data) => {
    try {
      // Generate a unique room ID if not provided
      const roomId = data.roomId || `duel_${Date.now()}_${Math.floor(Math.random() * 10000)}`;
      
      // Try to make the API call
      try {
        const response = await api.post(API_ENDPOINTS.duel.createRoom, {
          ...data,
          roomId // Include the roomId in the request
        });
        
        // Save to localStorage for offline/fallback use only
        if (response?.success && response.data) {
          saveRoomToLocalStorage(response.data);
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error creating room, using fallback:', apiError);
        
        // Create a fallback room object
        const fallbackRoom = {
          roomId,
          name: data.roomName,
          createdBy: {
            userId: data.userId,
            username: data.username
          },
          participants: [
            {
              userId: data.userId,
              username: data.username,
              joinedAt: new Date().toISOString(),
              isReady: false
            }
          ],
          maxParticipants: data.maxParticipants || 8,
          status: 'active',
          matches: [],
          chat: []
        };
        
        // Save fallback room to localStorage
        saveRoomToLocalStorage(fallbackRoom);
        
        // Return a successful fallback response
        return { 
          success: true, 
          data: fallbackRoom 
        };
      }
    } catch (error) {
      console.error('Error creating room:', error);
      throw error;
    }
  },

  /**
   * Get room details
   * @param {string} roomId - Room ID
   * @returns {Promise} - API response with room details
   */
  getRoomDetails: async (roomId) => {
    try {
      // Try to get room details from API first (MongoDB)
      try {
        const response = await api.get(API_ENDPOINTS.duel.roomDetails(roomId));
        
        // Save to localStorage as a fallback
        if (response?.success && response.data) {
          saveRoomToLocalStorage(response.data);
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error getting room details, trying localStorage:', apiError);
        
        // If API fails, try to get room from localStorage
        const localRoom = getRoomFromLocalStorage(roomId);
        
        if (localRoom) {
          console.log('Found room in localStorage:', roomId);
          return {
            success: true,
            data: localRoom
          };
        }
        
        // If room not found anywhere, return error
        return {
          success: false,
          error: 'Room not found'
        };
      }
    } catch (error) {
      console.error('Error getting room details:', error);
      throw error;
    }
  },

  /**
   * Join a duel room
   * @param {string} roomId - Room ID
   * @param {Object} data - Join data
   * @param {string} data.userId - User ID of the player joining
   * @param {string} data.username - Username of the player joining
   * @returns {Promise} - API response with updated room data
   */
  joinRoom: async (roomId, data) => {
    try {
      // Try API first
      try {
        const response = await api.post(API_ENDPOINTS.duel.joinRoom(roomId), data);
        
        // Save to localStorage as a fallback
        if (response?.success && response.data) {
          saveRoomToLocalStorage(response.data);
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error joining room, using localStorage:', apiError);
        
        // If API fails, try to modify the room in localStorage
        const localRoom = getRoomFromLocalStorage(roomId);
        
        if (localRoom) {
          // Check if the user is already in the room
          const existingParticipant = localRoom.participants.find(p => p.userId === data.userId);
          
          if (!existingParticipant) {
            // Add user to participants
            localRoom.participants.push({
              userId: data.userId,
              username: data.username,
              joinedAt: new Date().toISOString(),
              isReady: false
            });
            
            // Add system message
            if (!localRoom.chat) {
              localRoom.chat = [];
            }
            
            localRoom.chat.push({
              userId: "system",
              username: "System",
              message: `${data.username} has joined the room.`,
              timestamp: new Date().toISOString()
            });
            
            // Save updated room
            saveRoomToLocalStorage(localRoom);
          }
          
          return {
            success: true,
            data: localRoom
          };
        }
        
        // Room not found
        return {
          success: false,
          error: 'Room not found'
        };
      }
    } catch (error) {
      console.error('Error joining room:', error);
      throw error;
    }
  },

  /**
   * Leave a duel room
   * @param {string} roomId - Room ID
   * @param {Object} data - Leave data
   * @param {string} data.userId - User ID of the player leaving
   * @returns {Promise} - API response with updated room data
   */
  leaveRoom: async (roomId, data) => {
    try {
      // Try API first
      try {
        const response = await api.post(API_ENDPOINTS.duel.leaveRoom(roomId), data);
        
        // Update localStorage if API call was successful
        if (response?.success) {
          const localRoom = getRoomFromLocalStorage(roomId);
          
          if (localRoom) {
            // Remove user from participants
            localRoom.participants = localRoom.participants.filter(p => p.userId !== data.userId);
            
            // Add system message
            if (!localRoom.chat) {
              localRoom.chat = [];
            }
            
            const username = 'User'; // Default username if not found
            
            localRoom.chat.push({
              userId: "system",
              username: "System",
              message: `${username} has left the room.`,
              timestamp: new Date().toISOString()
            });
            
            // If no participants left, mark room as closed
            if (localRoom.participants.length === 0) {
              localRoom.status = 'closed';
              removeRoomFromLocalStorage(roomId);
            } else {
              saveRoomToLocalStorage(localRoom);
            }
          }
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error leaving room, using localStorage:', apiError);
        
        // If API fails, try to modify the room in localStorage
        const localRoom = getRoomFromLocalStorage(roomId);
        
        if (localRoom) {
          // Find username before removing participant
          const participant = localRoom.participants.find(p => p.userId === data.userId);
          const username = participant ? participant.username : 'User';
          
          // Remove user from participants
          localRoom.participants = localRoom.participants.filter(p => p.userId !== data.userId);
          
          // Add system message
          if (!localRoom.chat) {
            localRoom.chat = [];
          }
          
          localRoom.chat.push({
            userId: "system",
            username: "System",
            message: `${username} has left the room.`,
            timestamp: new Date().toISOString()
          });
          
          // If no participants left, mark room as closed
          if (localRoom.participants.length === 0) {
            localRoom.status = 'closed';
            removeRoomFromLocalStorage(roomId);
          } else {
            saveRoomToLocalStorage(localRoom);
          }
          
          return {
            success: true,
            data: {
              roomId,
              status: localRoom.status,
              message: 'Left room successfully'
            }
          };
        }
        
        // Room not found
        return {
          success: false,
          error: 'Room not found'
        };
      }
    } catch (error) {
      console.error('Error leaving room:', error);
      throw error;
    }
  },

  /**
   * Add chat message to a room
   * @param {string} roomId - Room ID
   * @param {Object} data - Message data
   * @param {string} data.userId - User ID of the sender
   * @param {string} data.username - Username of the sender
   * @param {string} data.message - Chat message
   * @returns {Promise} - API response with the new message
   */
  addChatMessage: async (roomId, data) => {
    try {
      // Try API first
      try {
        const response = await api.post(API_ENDPOINTS.duel.addChatMessage(roomId), data);
        
        // Update localStorage if API call was successful
        if (response?.success && response.data) {
          addChatMessageToRoom(roomId, response.data);
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error adding chat message, using localStorage:', apiError);
        
        // Create a message object
        const message = {
          userId: data.userId,
          username: data.username,
          message: data.message,
          timestamp: new Date().toISOString()
        };
        
        // Add message to localStorage
        addChatMessageToRoom(roomId, message);
        
        // Return a successful fallback response
        return { 
          success: true, 
          data: message 
        };
      }
    } catch (error) {
      console.error('Error adding chat message:', error);
      throw error;
    }
  },

  /**
   * Set participant ready status
   * @param {string} roomId - Room ID
   * @param {Object} data - Ready data
   * @param {string} data.userId - User ID of the participant
   * @param {boolean} data.isReady - Ready status
   * @returns {Promise} - API response with updated room data
   */
  setParticipantReady: async (roomId, data) => {
    try {
      // Try API first
      try {
        const response = await api.post(API_ENDPOINTS.duel.setReady(roomId), data);
        
        // Update localStorage if API call was successful
        if (response?.success) {
          updateParticipantReadyStatus(roomId, data.userId, data.isReady);
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error setting ready status, using localStorage:', apiError);
        
        // Update participant ready status in localStorage
        updateParticipantReadyStatus(roomId, data.userId, data.isReady);
        
        // Return a successful fallback response
        return { 
          success: true, 
          data: {
            roomId,
            participants: [
              {
                userId: data.userId,
                isReady: data.isReady
              }
            ]
          }
        };
      }
    } catch (error) {
      console.error('Error setting ready status:', error);
      throw error;
    }
  },

  /**
   * Get all active rooms
   * @returns {Promise} - API response with list of active rooms
   */
  // Debug flag - set to false to disable excessive logging
  DEBUG_ROOMS: false,
  
  getActiveRooms: async () => {
    try {
      // First, try to get rooms from the API (which pulls from MongoDB)
      // This should be the source of truth across all clients
      try {
        // Add a timestamp to prevent caching
        const timestamp = new Date().getTime();
        const roomsResponse = await api.get(`${API_ENDPOINTS.duel.rooms}?timestamp=${timestamp}`);
        
        if (roomsResponse?.success && Array.isArray(roomsResponse.data)) {
          // Update localStorage with the latest data from API for offline use
          roomsResponse.data.forEach(room => {
            saveRoomToLocalStorage(room);
          });
          
          // API data is our source of truth - return it
          return { success: true, data: roomsResponse.data };
        }
      } catch (apiError) {
        // Only log API errors when debug is enabled
        if (duelService.DEBUG_ROOMS) {
          console.warn('API error getting active rooms, falling back to local storage:', apiError);
        }
      }
      
      // API failed or returned invalid data - try localStorage as last resort
      if (duelService.DEBUG_ROOMS) {
        console.log('API unavailable, using localStorage...');
      }
      const localRooms = getAllRoomsFromLocalStorage().filter(room => room.status === 'active');
      
      // Only if we have no data at all, use demo rooms
      if (localRooms.length === 0) {
        const mockRooms = [
          { 
            roomId: 'demo1',
            name: 'Practice Room',
            createdBy: {
              userId: 'player1',
              username: 'PoolPro'
            },
            participants: [
              {
                userId: 'player1',
                username: 'PoolPro',
                joinedAt: new Date().toISOString(),
                isReady: false
              }
            ],
            maxParticipants: 8,
            status: 'active',
            matches: [],
            chat: []
          },
          { 
            roomId: 'demo2',
            name: 'Tournament Room',
            createdBy: {
              userId: 'player2',
              username: 'Challenger'
            },
            participants: [
              {
                userId: 'player2',
                username: 'Challenger',
                joinedAt: new Date().toISOString(),
                isReady: false
              }
            ],
            maxParticipants: 4,
            status: 'active',
            matches: [],
            chat: []
          }
        ];
        return { success: true, data: mockRooms };
      }
      
      return { success: true, data: localRooms };
    } catch (error) {
      console.error('Error getting active rooms:', error);
      // Even if there's an error, try to return local rooms as a last resort
      const localRooms = getAllRoomsFromLocalStorage().filter(room => room.status === 'active');
      return { success: true, data: localRooms.length > 0 ? localRooms : [] };
    }
  },

  /**
   * Get player match history
   * @param {string} userId - User ID
   * @param {number} limit - Number of matches to return (default 10)
   * @returns {Promise} - API response with match history
   */
  getMatchHistory: async (userId, limit = 10) => {
    try {
      return await api.get(`${API_ENDPOINTS.duel.history(userId)}?limit=${limit}`);
    } catch (error) {
      console.error('Error getting match history:', error);
      throw error;
    }
  },

  /**
   * Start a match
   * @param {string} matchId - Match ID
   * @returns {Promise} - API response with match data
   */
  startMatch: async (matchId) => {
    try {
      return await api.post(API_ENDPOINTS.duel.startMatch(matchId), {});
    } catch (error) {
      console.error('Error starting match:', error);
      throw error;
    }
  },

  /**
   * Update match results
   * @param {string} matchId - Match ID
   * @param {Object} data - Match result data
   * @param {string} data.winnerId - User ID of the winner
   * @param {string} data.loserId - User ID of the loser
   * @param {number} data.duration - Match duration in seconds
   * @returns {Promise} - API response with updated match data
   */
  updateMatchResults: async (matchId, data) => {
    try {
      return await api.put(API_ENDPOINTS.duel.results(matchId), data);
    } catch (error) {
      console.error('Error updating match results:', error);
      throw error;
    }
  },

  /**
   * Abort a match
   * @param {string} matchId - Match ID
   * @param {Object} data - Abort data
   * @param {string} data.reason - Reason for aborting the match
   * @returns {Promise} - API response with aborted match data
   */
  abortMatch: async (matchId, data = {}) => {
    try {
      return await api.put(API_ENDPOINTS.duel.abort(matchId), data);
    } catch (error) {
      console.error('Error aborting match:', error);
      throw error;
    }
  },

  /**
   * Update room status
   * @param {string} roomId - Room ID
   * @param {string} status - New status ('active', 'in_game', 'closed', etc.)
   * @returns {Promise} - API response with updated room data
   */
  updateRoomStatus: async (roomId, status) => {
    try {
      // Try API first
      try {
        const response = await api.put(API_ENDPOINTS.duel.roomDetails(roomId), { status });
        
        // Update localStorage if API call was successful
        if (response?.success && response.data) {
          const localRoom = getRoomFromLocalStorage(roomId);
          if (localRoom) {
            localRoom.status = status;
            saveRoomToLocalStorage(localRoom);
          }
        }
        
        return response;
      } catch (apiError) {
        console.warn('API error updating room status, using localStorage:', apiError);
        
        // If API fails, try to update the room in localStorage
        const localRoom = getRoomFromLocalStorage(roomId);
        
        if (localRoom) {
          // Update room status
          localRoom.status = status;
          
          // Add system message
          if (!localRoom.chat) {
            localRoom.chat = [];
          }
          
          localRoom.chat.push({
            userId: "system",
            username: "System",
            message: `Room status changed to ${status}.`,
            timestamp: new Date().toISOString()
          });
          
          // Save updated room
          saveRoomToLocalStorage(localRoom);
          
          return {
            success: true,
            data: localRoom
          };
        }
        
        // Room not found
        return {
          success: false,
          error: 'Room not found'
        };
      }
    } catch (error) {
      console.error('Error updating room status:', error);
      throw error;
    }
  }
};

export default duelService;
